import type {ReactNode} from "react";
import {Fragment, createRef} from "react";
import styled from "styled-components";
import {PointerDetectorReact} from "../../interaction/PointerDetectorReact";
import {FlexCenterStyle} from "../styles/styles";
import {colorPalette} from "../styles/colorPalette";
import {MathUtils} from "../../../utils/math/MathUtils";
import {ReactUtils} from "../../utils/ReactUtils";
import {useSplitterV5Layout} from "./useSplitterV5Layout";

const maxChildrenCount = 10;
const elementKeys = Array.from({length: maxChildrenCount}, () => MathUtils.getNewRandomGUID());
const splitterDragHandleRefObjects = Array.from({length: maxChildrenCount}, () => createRef<HTMLDivElement>());

type TSplitterV5 = {
	readonly children: ReactNode;
	readonly localStorageKey?: string;
};

const SPLITTER_LS_KEY = "v5-splitter-grid-ratios";

/**
 * Horizontal splitter: only this outer box uses CSS grid for column widths. What you put inside each pane can use its own layout.
 */
export const SplitterV5 = (props: TSplitterV5) => {
	const {children, localStorageKey = SPLITTER_LS_KEY} = props;

	const {
		rootRef,
		layoutTracks,
		gridTemplateColumns,
		activeDragHandlePaneIndex,
		setActiveDragHandlePaneIndex,
		savedBodyCursor,
		onHandlePointerDown,
		onHandlePointerUp,
		onHandlePointerMove,
	} = useSplitterV5Layout({
		children,
		localStorageKey,
		elementKeys,
	});

	return (
		<SplitterV5RootStyled
			style={{gridTemplateColumns}}
			ref={rootRef}
		>
			{layoutTracks.map((track) =>
				track.kind === "splitterPane" ? (
					<Fragment key={track.splitterPane.key}>{track.splitterPane.element}</Fragment>
				) : (
					<Fragment key={track.splitterLayoutKey}>
						<SplitterDragHandleStyled
							ref={splitterDragHandleRefObjects[track.splitterDragHandleDataId]}
							data-id={track.splitterDragHandleDataId}
							className={ReactUtils.cls({
								isActive: track.splitterPaneIndex === activeDragHandlePaneIndex,
							})}
						/>
						<PointerDetectorReact
							onDown={() => {
								onHandlePointerDown();
								setActiveDragHandlePaneIndex(track.splitterPaneIndex);
								savedBodyCursor.current = document.body.style.cursor;
								document.body.style.cursor = "col-resize";
							}}
							onMove={onHandlePointerMove}
							onUp={() => {
								onHandlePointerUp();
								setActiveDragHandlePaneIndex(-1);
								document.body.style.cursor = savedBodyCursor.current;
							}}
							refObject={splitterDragHandleRefObjects[track.splitterDragHandleDataId]}
						/>
					</Fragment>
				),
			)}
		</SplitterV5RootStyled>
	);
};

const SplitterV5RootStyled = styled.div`
	display: grid;
	min-height: 0;
	flex: 1;

	.mainpanel {
		position: relative;
	}
`;

const SplitterDragHandleStyled = styled.div`
	${FlexCenterStyle};
	width: 2px;
	background-color: ${colorPalette.gray.c300};
	cursor: col-resize;
	z-index: 1;

	&:hover,
	&.isActive {
		background-color: ${colorPalette.primary.c500Primary};
	}
`;
